import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Scissors, CheckCircle2, RotateCcw, Factory, Download } from "lucide-react";
import { toast } from "sonner";
import {
  getPublicWorkshopQuote,
  togglePublicWorkshopCutCheck,
  getPublicCutSheetPdfUrl,
} from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const WorkshopView = () => {
  const { quoteId, token } = useParams();
  const [quote, setQuote] = useState(null);
  const [checks, setChecks] = useState(new Set());
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    getPublicWorkshopQuote(quoteId, token)
      .then((q) => {
        setQuote(q);
        setChecks(new Set(q.cut_checks || []));
        setStatus("ok");
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Link invalid or expired";
        setErrorMsg(typeof detail === "string" ? detail : "Link invalid");
        setStatus("error");
      });
  }, [quoteId, token]);

  const totalPieces = useMemo(() => {
    if (!quote) return 0;
    return (quote.rooms || []).reduce(
      (sum, r) => sum + (r.cut_pieces?.length || 0),
      0
    );
  }, [quote]);

  // Lookup: screen-type key → human label
  const typeLabel = useMemo(() => {
    const map = {};
    (quote?.screen_types || []).forEach((t) => {
      map[t.key] = t.label;
    });
    return map;
  }, [quote]);

  const doneCount = checks.size;
  const progress = totalPieces
    ? Math.round((doneCount / totalPieces) * 100)
    : 0;

  const toggle = async (key) => {
    const currentlyDone = checks.has(key);
    const nextDone = !currentlyDone;
    // Optimistic update
    const next = new Set(checks);
    if (nextDone) next.add(key);
    else next.delete(key);
    setChecks(next);
    try {
      const res = await togglePublicWorkshopCutCheck(
        quoteId,
        token,
        key,
        nextDone
      );
      setChecks(new Set(res.cut_checks || []));
      if (res.produced_at && !quote.produced_at) {
        setQuote({ ...quote, produced_at: res.produced_at });
        toast.success("All pieces cut — marked as produced");
      } else if (!res.produced_at && quote.produced_at) {
        setQuote({ ...quote, produced_at: null });
      }
    } catch {
      // Revert
      setChecks(checks);
      toast.error("Couldn't save — check your internet");
    }
  };

  if (status === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="workshop-loading"
      >
        <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.2em]">
          Loading cut list…
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white px-6"
        data-testid="workshop-error"
      >
        <div className="max-w-sm text-center">
          <div className="w-12 h-12 mx-auto mb-6 border-2 border-red-500 text-red-500 flex items-center justify-center">
            <Scissors size={20} />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">
            Link invalid
          </h1>
          <p className="text-sm text-zinc-600 font-mono">{errorMsg}</p>
          <p className="text-xs text-zinc-400 font-mono mt-4">
            Ask the owner for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="workshop-view">
      {/* Force portrait orientation when this workshop page is printed. */}
      <style>{`@media print { @page { size: A4 portrait; margin: 8mm; } }`}</style>
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#09090b] text-white flex items-center justify-center">
              <Factory size={18} />
            </div>
            <div>
              <div className="font-display text-lg font-black leading-none tracking-tighter">
                WORKSHOP
              </div>
              <div className="label-xs mt-1">Cut List · Read + Tick</div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-black leading-[0.95] tracking-tighter mb-2">
            {quote.client_name || "Cut list"}
          </h1>
          <div className="font-mono text-xs text-zinc-500 flex flex-wrap gap-x-4 gap-y-1">
            {quote.area ? <span>{quote.area}</span> : null}
            {quote.color ? <span>Color: {quote.color}</span> : null}
            <span>{fmtDate(quote.created_at)}</span>
          </div>

          <a
            href={getPublicCutSheetPdfUrl(quoteId, token)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 border border-[#09090b] bg-[#09090b] text-white px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#27272a] transition-colors"
            data-testid="workshop-save-pdf-btn"
          >
            <Download size={13} /> Save PDF
          </a>

          {/* Progress */}
          <div
            className="mt-6 border border-zinc-200 p-4"
            data-testid="workshop-progress"
          >
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="label-xs">Progress</div>
                <div className="font-display text-3xl font-black tracking-tighter leading-none mt-1">
                  {doneCount}
                  <span className="text-zinc-300">/{totalPieces}</span>
                </div>
              </div>
              {quote.produced_at ? (
                <div className="inline-flex items-center gap-2 border border-green-600 text-green-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold">
                  <CheckCircle2 size={12} /> Done {fmtDate(quote.produced_at)}
                </div>
              ) : null}
            </div>
            <div className="h-1 bg-zinc-100">
              <div
                className="h-full bg-[#09090b] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Rooms */}
      <main className="max-w-4xl mx-auto p-6 md:p-8 space-y-8">
        {(quote.rooms || []).map((room, idx) => {
          const pieces = room.cut_pieces || [];
          const roomDone = pieces.every((_, pi) =>
            checks.has(`${room.id}:${pi}`)
          );
          return (
            <section
              key={room.id}
              className={`border ${
                roomDone ? "border-green-500 bg-green-50/30" : "border-zinc-200"
              } p-5`}
              data-testid={`workshop-room-${idx}`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-4">
                <div>
                  <div className="font-mono text-[11px] text-zinc-400 uppercase tracking-[0.18em]">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-display text-2xl font-black tracking-tighter mt-1">
                    {room.name}
                  </h2>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {room.screen_type ? (
                      <span
                        data-testid={`workshop-room-type-${idx}`}
                        className="inline-flex items-center px-2 py-1 bg-[#09090b] text-white font-mono text-[10px] font-bold tracking-[0.18em] uppercase"
                      >
                        {typeLabel[room.screen_type] || room.screen_type}
                      </span>
                    ) : (
                      <span
                        data-testid={`workshop-room-type-${idx}`}
                        className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-900 border border-amber-300 font-mono text-[10px] font-bold tracking-[0.18em] uppercase"
                      >
                        ⚠ No screen type
                      </span>
                    )}
                    <span className="font-mono text-xs text-zinc-500">
                      {room.width_mm} × {room.height_mm} mm
                    </span>
                  </div>
                </div>
                {roomDone ? (
                  <CheckCircle2 size={20} className="text-green-600" />
                ) : null}
              </div>

              {pieces.length === 0 ? (
                <div className="font-mono text-xs text-zinc-400 italic">
                  No cut pieces defined for this screen type.
                </div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {pieces.map((p, pi) => {
                    const key = `${room.id}:${pi}`;
                    const done = checks.has(key);
                    const shape = (room.piece_shapes || {})[p.label] || "";
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          data-testid={`workshop-tick-${idx}-${pi}`}
                          className={`w-full flex items-center gap-4 py-3 text-left hover:bg-zinc-50 transition-colors ${
                            done ? "opacity-50" : ""
                          }`}
                        >
                          <span
                            className={`flex-none w-6 h-6 border-2 flex items-center justify-center ${
                              done
                                ? "bg-[#09090b] border-[#09090b] text-white"
                                : "border-zinc-300"
                            }`}
                          >
                            {done ? <CheckCircle2 size={14} /> : null}
                          </span>
                          <span className="flex-none font-mono text-sm text-zinc-500 w-8">
                            {p.qty}×
                          </span>
                          <span
                            className={`flex-1 font-mono text-base ${
                              done ? "line-through" : ""
                            }`}
                          >
                            {p.length_mm} mm
                          </span>
                          {shape ? (
                            <span
                              className="flex-none inline-flex items-center justify-center w-7 h-7 bg-orange-500 text-white font-display font-black text-sm leading-none"
                              title={`${shape}-shape`}
                            >
                              {shape}
                            </span>
                          ) : null}
                          <span className="flex-none text-xs uppercase tracking-[0.12em] font-semibold text-zinc-500">
                            {p.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {room.sketch_data_url ? (
                <div className="mt-4 border border-zinc-200">
                  <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-600">
                    Sketch / Notes
                  </div>
                  <img
                    src={room.sketch_data_url}
                    alt={`Sketch for ${room.name}`}
                    className="w-full block bg-white"
                  />
                </div>
              ) : null}
            </section>
          );
        })}
      </main>

      <footer className="max-w-4xl mx-auto p-6 md:p-8 border-t border-zinc-200 mt-8">
        <div className="font-mono text-[11px] text-zinc-400 leading-relaxed flex items-center gap-2">
          <RotateCcw size={11} />
          Changes save automatically. This page shows the cut list only — no
          prices.
        </div>
      </footer>
    </div>
  );
};

export default WorkshopView;
